/**
 * System Administration Help Content
 * 
 * Feature: 136-dashboard-alignment (T057)
 * Pages: /alerts, /admin/clients, /admin/users, /audit, /settings
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const systemHelp: PageHelpContent = {
  pageTitle: 'System Administration',
  purpose: 'Manage alerts, clients, users, audit logs, and system settings for the Fabric Control Plane.',
  sections: [
    {
      title: 'Alert Management',
      paragraphs: [
        'View and manage system alerts triggered by capacity issues, backup failures, SLA violations, or infrastructure degradation. Alerts can be acknowledged, investigated, and resolved.',
      ],
      concepts: [
        { term: 'Critical Alert', definition: 'Requires immediate attention. Triggered by Type 1/4 capacity failures, SLA breaches, or infrastructure outages.' },
        { term: 'Warning Alert', definition: 'Indicates a potential issue that may escalate. Triggered by performance degradation or approaching thresholds.' },
        { term: 'Info Alert', definition: 'Informational notification about system events. No action required.' },
      ],
    },
    {
      title: 'Client Administration',
      paragraphs: [
        'Manage client accounts, their capacity assignments, and SLA configurations. Each client can have dedicated or shared capacities.',
      ],
    },
    {
      title: 'User Management',
      concepts: [
        { term: 'Admin', definition: 'Full system access including user management, system settings, and all operational controls.' },
        { term: 'Operator', definition: 'Day-to-day operational access: run jobs, manage backups, acknowledge alerts.' },
        { term: 'Manager', definition: 'Business-level access: view reports, cost analysis, SLA compliance. No operational controls.' },
        { term: 'User', definition: 'Read-only access to assigned resources with personal workspace favorites and activity feed.' },
        { term: 'Viewer', definition: 'Minimal read-only access to the dashboard and basic resource views.' },
      ],
    },
    {
      title: 'Audit Log',
      paragraphs: [
        'All user actions and system events are recorded in the audit log. Use filters to search by user, action type, timestamp, or resource. Logs are retained for 90 days by default.',
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Navigate to the Alerts page to review all active alerts, filtering by severity (Critical, Warning, Info) as needed.' },
        { step: 2, instruction: 'Acknowledge or investigate critical alerts first — click the alert row to see details and affected resources.' },
        { step: 3, instruction: 'Go to Admin > Clients to manage client accounts, update capacity assignments, or adjust SLA configurations.' },
        { step: 4, instruction: 'Open the Audit Log and use filters (user, action type, date range) to search for specific system events.' },
        { step: 5, instruction: 'Use Admin > Users to review role assignments and ensure each user has the appropriate access level.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Service Principals', href: '/service-principals' },
        { label: 'Infrastructure', href: '/infrastructure' },
      ],
    },
  ],
};

export default systemHelp;
